import type { ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';

import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import ListItemIcon from '@mui/material/ListItemIcon';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import type { ButtonProps } from '@mui/material/Button';

import { ensureDefined, invariant } from '@e-socna/utils';

import { OPEN } from './Access/utils/access-gate.util';
import { MessageList } from './MessageList';
import type { AccessGate } from './Access/types/access-gate.type';

interface Option {
  readonly icon?: ReactNode;
  readonly key: string;
  readonly label: string;
}

interface Props {
  readonly options: readonly Option[];
  readonly onClick: (selectedOption: Option) => void;
  readonly ariaLabelButtonGroup: string;
  readonly ariaLabelButton: string;
  readonly disabled?: boolean;
  // Closed when the user may not do it, as a GatedButton: greyed out, the tooltip giving the reasons.
  readonly gate?: AccessGate;
  readonly id: string;
  readonly size?: ButtonProps['size'];
}

export type { Option as SplitButtonOption };

export const SplitButton = ({
  options,
  onClick: handleClick,
  ariaLabelButtonGroup,
  ariaLabelButton,
  disabled = false,
  gate = OPEN,
  id,
  size = 'large',
}: Readonly<Props>) => {
  invariant(!!options.length, 'SplitButton must have at least one option');

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  // A menu option runs right away (action menu convention), the main button
  // keeping the default action.
  const handleMenuItemClick = useCallback(
    (option: Readonly<Option>) => {
      setOpen(false);
      handleClick(option);
    },
    [handleClick],
  );

  const handleToggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const handleClose = useCallback((event: Readonly<Event>) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  }, []);

  const { defaultOption } = ensureDefined({ defaultOption: options[0] });

  const clickHandler = useCallback(() => {
    handleClick(defaultOption);
  }, [handleClick, defaultOption]);

  return (
    <>
      <Tooltip
        title={gate.isAllowed ? '' : <MessageList messages={gate.reasons} />}
      >
        <span>
          <ButtonGroup
            aria-label={ariaLabelButtonGroup}
            disabled={disabled || !gate.isAllowed}
            ref={anchorRef}
            size={size}
            variant="contained"
          >
            <Button onClick={clickHandler} startIcon={defaultOption.icon}>
              {defaultOption.label}
            </Button>
            <Button
              aria-controls={open ? `${id}-menu` : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-haspopup="menu"
              aria-label={ariaLabelButton}
              onClick={handleToggle}
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
        </span>
      </Tooltip>
      <Popper
        // TODO : check if we can refactor this code in order to respect this rule
        // eslint-disable-next-line react-hooks/refs
        anchorEl={anchorRef.current}
        disablePortal
        open={open}
        role={undefined}
        sx={{
          zIndex: 1,
        }}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...TransitionProps}
            // eslint-disable-next-line react/forbid-component-props
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem id={`${id}-menu`}>
                  {options.map((option) => (
                    <MenuItem
                      key={option.key}
                      // eslint-disable-next-line react/jsx-no-bind
                      onClick={() => {
                        handleMenuItemClick(option);
                      }}
                    >
                      {!!option.icon && (
                        <ListItemIcon>{option.icon}</ListItemIcon>
                      )}
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
